<template>
  <div class="wiki">
    <div
      v-for="(item, index) in finalWikiData"
      :key="`wiki_${index}`"
      class="wiki-block"
      @click.stop="openWiki(item.id, item.isOutdated)"
    >
      <div class="left_block">
        <icons-provider
          :icon-props="{
            width: '40',
            height: '40',
          }"
          :name="'wiki'"
          class="icon"
        />
        <ion-button
          v-if="item.isOutdated"
          v-tooltip.bottom="{
            content: t('wiki.editOptions.outdatedExplanation'),
            theme: 'info-tooltip',
          }"
          class="outdated"
          size="small"
          @click="toCurrentVersion(item)"
        >
          {{ t('wiki.editOptions.outdated') }}
        </ion-button>
      </div>

      <div class="info">
        <ion-label class="title">{{ item.name }} </ion-label>
        <ion-text class="text" v-html="getInfo(item)" />
      </div>

      <div class="right_block">
        <ion-button
          v-if="status !== FileStatusEnum.Loading"
          size="default"
          fill="clear"
          class="download-button"
          @click.stop="downloadWiki(item)"
        >
          <ion-icon slot="icon-only" :icon="downloadOutline" />
        </ion-button>
        <ion-button
          v-if="status === FileStatusEnum.Loading"
          size="default"
          fill="clear"
          class="download-button"
          @click.stop="stopDownload()"
        >
          <ion-icon slot="icon-only" :icon="closeOutline" />
        </ion-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IonText, IonLabel, IonButton, IonIcon } from '@ionic/vue';
import { closeOutline, downloadOutline } from 'ionicons/icons';
import type { PropType } from 'vue';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { IconsProvider } from '@/components';
import { FileStatusEnum, DocumentExtensionEnum } from '@/enums';
import { WikiActionEnum, WikiVersionEnum } from '@/enums/wiki';
import { useFilesHybrid, useToasts, useWiki } from '@/helpers';
import { isBlob } from '@/helpers/guards';
import { useI18n } from '@/i18n';
import { useWikiStore } from '@/store';
import { WikiActionPayload, WikiModel } from '@/types';

//#region Props
const props = defineProps({
  wikiData: {
    type: Object as PropType<WikiModel[]>,
    required: true,
  },
  dateToCompare: {
    type: String,
    required: true,
  },
});
//#endregion

//#region Variables
const route = useRoute();

const { t } = useI18n();
const { showSonnerToast } = useToasts();
const wikiHelper = useWiki();

const wikiStore = useWikiStore();

const status = ref<FileStatusEnum>(FileStatusEnum.Init);
const finalWikiData = ref<(WikiModel & { isOutdated: boolean })[]>([]);

//#region Methods
const getInfo = (item: WikiModel) => {
  if (item.version === WikiVersionEnum.V1) return item.bodyHtml;

  if (item.version === WikiVersionEnum.V2) {
    const info: string[] = [];
    item.wikiContent.body.forEach((b) => {
      info.push(b.name);
    });

    return info;
  }

  return '';
};

const openWiki = async (id: number, isOutdated: boolean) => {
  let payload: WikiActionPayload;
  if (isOutdated) {
    payload = {
      type: WikiActionEnum.ToHistorical,
      id,
      date: props.dateToCompare,
      routeName: String(route.name),
    };
  } else {
    payload = { type: WikiActionEnum.ToCurrent, id };
  }

  await wikiHelper.handleAction(payload);
};

const downloadWiki = async (item: WikiModel) => {
  if (status.value === FileStatusEnum.Loading) {
    return;
  }

  status.value = FileStatusEnum.Loading;
  emit('onLoading', true, item.id);

  const response = await wikiStore.download(item.id, DocumentExtensionEnum.PDF);
  if (isBlob(response)) {
    status.value = await useFilesHybrid().downloadWiki(item, response as Blob);
    showSonnerToast(t('files.successDownloaded'), status.value === FileStatusEnum.Success);
  }

  showSonnerToast(t('files.successDownloaded'), status.value === FileStatusEnum.Success);
};

const stopDownload = async () => {
  status.value = FileStatusEnum.Success;
  showSonnerToast(t('files.downloadStopped'), true);
};

const toCurrentVersion = async (wiki: WikiModel): Promise<void> => {
  wikiHelper.handleAction({ type: WikiActionEnum.ToCurrent, id: wiki.id });
};
//#endregion

//#region Lifecycle
onMounted(() => {
  props.wikiData.forEach((wiki) => {
    const isOutdated = new Date(wiki.editedAt).getTime() > new Date(props.dateToCompare).getTime();
    finalWikiData.value.push({ ...wiki, isOutdated });
  });
});
//#endregion

//#region Emits
const emit = defineEmits(['onLoading']);
//#endregion
</script>

<style scoped lang="scss">
.wiki {
  margin-top: 1rem;

  .wiki-block {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    padding-right: 0;
    border: 1px solid var(--ion-color-light-custom);
    border-radius: 4px;

    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }

    .left_block {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      .icon {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: start;
        flex-shrink: 0;

        ion-icon {
          font-size: 2.5rem;
          width: 100%;
        }
      }

      .outdated {
        @include resetStyleFromIonicButton;
        text-transform: unset;
        font-size: 0.5rem;
        width: 40px;
        --color: var(--ion-color-custom);
        --border-radius: 4px;
        --background: var(--ion-color-notification-badge);
      }
    }

    .info {
      margin-left: 1rem;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      overflow: hidden;

      ion-text {
        --webkit-line-clamp: 3;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        color: var(--ion-color-medium);
      }
    }

    &:hover {
      cursor: pointer;
      opacity: 0.7;
    }

    .right_block {
      display: flex;
      align-items: center;
      margin-left: auto;
      margin-right: 0;

      .download-button {
        ion-icon {
          color: var(--ion-color-medium);
        }
      }
    }
  }
}
</style>
